import { useAuthStore } from '../store/authStore';
import { useLocation } from 'react-router-dom';

export const useAuth = () => {
  const location = useLocation();
  const {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout: originalLogout,
    setError,
    clearError
  } = useAuthStore();

  // Função de logout inteligente que preserva o slug
  const logout = () => {
    const currentPath = location.pathname;
    
    // Extrair slug da URL atual
    const segments = currentPath.split('/').filter(segment => segment);
    const possibleSlug = segments[0];
    
    // Se há um slug válido (não é uma rota padrão), redirecionar para /:slug/login
    if (possibleSlug && possibleSlug !== 'login' && possibleSlug !== 'dashboard' && 
        possibleSlug !== 'users' && possibleSlug !== 'clients' && possibleSlug !== 'resellers' && 
        possibleSlug !== 'settings' && possibleSlug !== 'tickets') {
      originalLogout(`/${possibleSlug}/login`);
    } else if (user?.cmsSlug) {
      // Se não conseguir extrair da URL, usar o slug do usuário
      originalLogout(`/${user.cmsSlug}/login`);
    }
  };

  // Funções auxiliares para verificar permissões
  const isAdmin = () => {
    // Apenas member_group_id === 1 é admin (Number evita falha com string)
    return Number(user?.member_group_id) === 1;
  };

  const isSocio = () => {
    return Number(user?.member_group_id) === 2;
  };

  const isMaster = () => {
    return Number(user?.member_group_id) === 3;
  };

  const isReseller = () => {
    return Number(user?.member_group_id) === 4;
  };

  const isSubReseller = () => {
    return Number(user?.member_group_id) === 6;
  };

  // Verificar permissão específica
  const hasPermission = (permission: string) => {
    if (!user) return false;

    // Hierarquia do XUI é a fonte principal
    const memberGroupId = Number(user.member_group_id || 0);
    if (memberGroupId === 1) return true; // admin

    const isResellerOrAbove = [2, 3, 4, 6].includes(memberGroupId);

    // Permissões legadas do painel, agora derivadas da hierarquia XUI
    const xuiMappedPermissions = new Set([
      'can_view_vod',
      'can_download',
      'can_delete_users',
      'can_change_username',
      'can_change_password',
      'can_change_bouquets',
      'can_restrict'
    ]);

    if (xuiMappedPermissions.has(permission)) {
      return isResellerOrAbove;
    }

    // Fallback de compatibilidade para permissões customizadas antigas
    if (user.group && user.group.permissions) {
      return user.group.permissions[permission as keyof typeof user.group.permissions] === true;
    }

    return false;
  };

  // Verificar se pode gerenciar usuários
  const canManageUsers = () => {
    return isAdmin();
  };

  // Verificar se pode criar sub-revendedores
  const canCreateSubResellers = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  // Verificar se pode ver revendedores
  const canViewResellers = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  // Verificar se pode ver configurações (revendedores e sub-revendedores também)
  const canViewSettings = () => {
    return isAdmin() || isSocio() || isMaster() || isReseller() || isSubReseller();
  };

  // Verificar se pode gerenciar tickets
  const canManageTickets = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  // Solicitações: só admin gere pedidos de todos; restantes só o próprio (via canManageItem)
  const canManageContentRequests = () => {
    return isAdmin();
  };

  // Qualquer revendedor+ pode criar/votar/acompanhar
  const canViewContentRequests = () => {
    return isAdmin() || isSocio() || isMaster() || isReseller() || isSubReseller();
  };

  // Verificar se pode responder tickets
  const canRespondTickets = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  // Verificar se pode encerrar tickets
  const canCloseTickets = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  // Verificar se pode deletar tickets
  const canDeleteTickets = () => {
    return isAdmin() || isSocio() || isMaster();
  };

  /** Remover registros em Pagamentos loja (admin, sócio, master — igual à API authorize('master')). */
  const canManageStorePayments = () => {
    if (isAdmin() || isSocio() || isMaster()) return true;
    const mg = Number(user?.member_group_id || 0);
    if ([1, 2, 3].includes(mg)) return true;
    if (user?.hierarchy?.isAdmin || user?.hierarchy?.isMaster) return true;
    const level = String(getUserLevel() || '').toLowerCase();
    if (['admin', 'socio', 'master'].includes(level)) return true;
    const role = String(user?.role || user?.level || '').toLowerCase();
    return ['admin', 'socio', 'master'].includes(role);
  };

  // Verificar se pode ver VOD
  const canViewVod = () => {
    return hasPermission('can_view_vod');
  };

  // Verificar se pode fazer download
  const canDownload = () => {
    return hasPermission('can_download');
  };

  // Verificar se pode deletar usuários
  const canDeleteUsers = () => {
    return hasPermission('can_delete_users');
  };

  // Verificar se pode alterar username
  const canChangeUsername = () => {
    return hasPermission('can_change_username');
  };

  // Verificar se pode alterar senha
  const canChangePassword = () => {
    return hasPermission('can_change_password');
  };

  // Verificar se pode alterar bouquets
  const canChangeBouquets = () => {
    return hasPermission('can_change_bouquets');
  };

  // Verificar se pode aplicar restrições
  const canRestrict = () => {
    return hasPermission('can_restrict');
  };

  // Obter informações do grupo
  const getGroupInfo = () => {
    return user?.group;
  };

  // Obter nível hierárquico
  const getUserLevel = () => {
    // Verificar níveis baseados no member_group_id (prioridade)
    if (user?.member_group_id === 1) return 'admin';
    if (user?.member_group_id === 2) return 'socio';
    if (user?.member_group_id === 3) return 'master';
    if (user?.member_group_id === 4) return 'reseller';
    if (user?.member_group_id === 6) return 'subreseller';
    
    // Fallback para hierarchy.level se member_group_id não estiver disponível
    const hierarchyLevel = user?.hierarchy?.level;
    return hierarchyLevel || 'user';
  };

  // Obter cor do grupo
  const getGroupColor = () => {
    return user?.group?.group_color || '#6B7280';
  };

  // Obter nome do grupo
  const getGroupName = () => {
    return user?.group?.group_name || 'Usuário';
  };

  return {
    // Estado
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    
    // Ações
    login,
    logout,
    setError,
    clearError,
    
    // Verificações de nível
    isAdmin,
    isSocio,
    isMaster,
    isReseller,
    isSubReseller,
    
    // Verificações de permissão
    hasPermission,
    canManageUsers,
    canCreateSubResellers,
    canViewResellers,
    canViewSettings,
    canViewVod,
    canDownload,
    canDeleteUsers,
    canChangeUsername,
    canChangePassword,
    canChangeBouquets,
    canRestrict,
    
    // Verificações de tickets
    canManageTickets,
    canRespondTickets,
    canCloseTickets,
    canDeleteTickets,
    canManageStorePayments,
    canManageContentRequests,
    canViewContentRequests,
    
    // Informações do grupo
    getGroupInfo,
    getUserLevel,
    getGroupColor,
    getGroupName,
  };
};
